// InfoDialog.js
import React from 'react';
import { BrandButton } from '../button';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import DnsIcon from '@mui/icons-material/Dns';
import GroupIcon from '@mui/icons-material/Group';
import TemplateIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import { acme } from '../../theme/acmeTheme';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  '&:first-of-type': {
    marginTop: 0
  }
}));

const InfoDialog = ({ open, onClose, stepId, stepTitle }) => {
  const getStepContent = () => {
    switch (stepId) {
      case 'domains':
        return {
          icon: <DnsIcon sx={{ color: acme.colors.brand }} />,
          description: 'Email domains are essential for professional email delivery and building trust with your recipients.',
          sections: [
            {
              title: 'What is a Domain?',
              icon: <InfoIcon sx={{ color: '#1976d2' }} />,
              content: [
                'A domain is your website address (like company.com)',
                'It identifies your business in email addresses (name@company.com)',
                'Using your own domain looks more professional than Gmail or Yahoo',
                'It helps improve email deliverability and reduces spam complaints'
              ]
            },
            {
              title: 'What are DNS Records?',
              icon: <SecurityIcon sx={{ color: '#f57c00' }} />,
              content: [
                'DNS records are settings that tell email providers your emails are legitimate',
                'SPF Record: Lists which servers can send emails from your domain',
                'DKIM Record: Adds a digital signature to verify email authenticity',
                'DMARC Record: Tells email providers what to do with unauthorized emails',
                'These records prevent your emails from going to spam folders'
              ]
            },
            {
              title: 'How to Add Your Domain',
              icon: <LightbulbIcon sx={{ color: '#4caf50' }} />,
              content: [
                '1. Click "Set Up" and enter your domain name (exampla.com)',
                '2. Copy the DNS records we provide',
                '3. Log into your domain provider (Zone, Wavecom, Planet etc.)',
                '4. Find the DNS settings, DNS server or DNS management section',
                '5. Add the records we provided exactly as shown',
                '6. Wait 24-48 hours for DNS propagation',
                '7. Come back and verify your records are working'
              ]
            }
          ],
          tips: [
            'Start with one domain - you can add more later',
            'Contact your domain provider support if you need help adding DNS records',
            'DNS changes can take up to 48 hours to take effect worldwide'
          ],
          warnings: [
            'Never share your DKIM private key with anyone',
            'Make sure to add records exactly as provided - any typos will cause failures'
          ]
        };

      case 'emails':
        return {
          icon: <EmailIcon sx={{ color: acme.colors.brand }} />,
          description: 'Verified sender emails are required to send campaigns and build trust with recipients.',
          sections: [
            {
              title: 'Why Verify Email Addresses?',
              icon: <SecurityIcon sx={{ color: '#1976d2' }} />,
              content: [
                'Email verification proves you own the email address',
                'Verified emails have better deliverability rates',
                'Recipients trust emails from verified senders',
                'Required by anti-spam laws and email providers'
              ]
            },
            {
              title: 'How Email Verification Works',
              icon: <InfoIcon sx={{ color: '#f57c00' }} />,
              content: [
                'You add your email address to our system',
                'We send a verification link to that email',
                'Click the link to confirm you own the email',
                'Your email is now verified and ready to use',
                'You can add multiple email addresses for different campaigns'
              ]
            },
            {
              title: 'Step-by-Step Guide',
              icon: <LightbulbIcon sx={{ color: '#4caf50' }} />,
              content: [
                '1. Click "Set Up" to go to the emails page',
                '2. Click "Add Email" button',
                '3. Enter your email address (use your domain email for best results)',
                '4. Add sender name (how recipients will see your name)',
                '5. Upload an avatar image (optional but recommended)',
                '6. Click "Add Email" to save',
                '7. Check your email inbox for verification link',
                '8. Click the verification link to confirm'
              ]
            }
          ],
          tips: [
            'Use your business email address (name@yourdomain.com) for better trust',
            'Add a professional avatar photo to increase open rates',
            'You can add multiple emails for different types of campaigns'
          ],
          warnings: [
            'Always check spam folder for verification emails',
            'Verification links expire after 24 hours - request a new one if needed'
          ]
        };

      case 'contacts':
        return {
          icon: <GroupIcon sx={{ color: acme.colors.brand }} />,
          description: 'Organize your email subscribers into groups for targeted and effective email campaigns.',
          sections: [
            {
              title: 'What are Contact Groups?',
              icon: <InfoIcon sx={{ color: '#1976d2' }} />,
              content: [
                'Groups help you organize contacts by interests, location, or behavior',
                'Send targeted emails to specific groups for better engagement',
                'Track which groups perform best with your content',
                'Easier to manage unsubscribes and preferences'
              ]
            },
            {
              title: 'How to Import Contacts',
              icon: <LightbulbIcon sx={{ color: '#4caf50' }} />,
              content: [
                '1. Click "Set Up" to go to contacts page',
                '2. Click "Import Contacts" button',
                '3. Upload a CSV file with email addresses',
                '4. Map your CSV columns to our contact fields',
                '5. Choose which group to add contacts to',
                '6. Review the import preview',
                '7. Click "Import" to add contacts to your list'
              ]
            },
            {
              title: 'CSV File Format',
              icon: <WarningIcon sx={{ color: '#f57c00' }} />,
              content: [
                'First row should contain column headers',
                'Required: Email column',
                'Optional: First Name, Last Name, Company, Phone',
                'Save as CSV format from Excel or Google Sheets',
                'Maximum 10,000 contacts per import'
              ]
            }
          ],
          tips: [
            'Start with a small test group of 10-50 contacts',
            'Create groups like "Customers", "Prospects", "Newsletter Subscribers"',
            'Clean your list regularly - remove bounced and unsubscribed emails'
          ],
          warnings: [
            'Only import contacts who gave permission to receive emails',
            'Follow GDPR and CAN-SPAM compliance rules',
            'Never buy email lists - use only your own contacts'
          ]
        };

      case 'templates':
        return {
          icon: <TemplateIcon sx={{ color: acme.colors.brand }} />,
          description: 'Create beautiful, responsive email templates that look great on all devices.',
          sections: [
            {
              title: 'Why Use Email Templates?',
              icon: <InfoIcon sx={{ color: '#1976d2' }} />,
              content: [
                'Templates save time when creating campaigns',
                'Ensure consistent branding across all emails',
                'Mobile-responsive design works on all devices',
                'Professional appearance increases engagement'
              ]
            },
            {
              title: 'Template Creation Options',
              icon: <LightbulbIcon sx={{ color: '#4caf50' }} />,
              content: [
                '1. Use our drag-and-drop editor (recommended for beginners)',
                '2. Choose from pre-built template library',
                '3. Import HTML template if you have one',
                '4. Start with blank template for full customization'
              ]
            },
            {
              title: 'Design Best Practices',
              icon: <TipsAndUpdatesIcon sx={{ color: '#f57c00' }} />,
              content: [
                'Keep it simple - avoid cluttered designs',
                'Use your brand colors and logo',
                'Include clear call-to-action buttons',
                'Make sure text is readable on mobile devices',
                'Test your template before sending campaigns'
              ]
            }
          ],
          tips: [
            'Start with our newsletter template - it works for most businesses',
            'Always preview your template on mobile and desktop',
            'Save templates with descriptive names for easy finding later'
          ],
          warnings: [
            'Avoid using too many images - they may not display properly',
            'Test links and buttons before using in campaigns'
          ]
        };

      case 'campaigns':
        return {
          icon: <CampaignIcon sx={{ color: acme.colors.brand }} />,
          description: 'Create and send your first email campaign to engage with your audience.',
          sections: [
            {
              title: 'What is an Email Campaign?',
              icon: <InfoIcon sx={{ color: '#1976d2' }} />,
              content: [
                'A campaign is an email sent to a group of contacts',
                'Can be newsletters, promotions, announcements, or updates',
                'Track opens, clicks, and engagement metrics',
                'Build relationships with your audience over time'
              ]
            },
            {
              title: 'Campaign Creation Steps',
              icon: <LightbulbIcon sx={{ color: '#4caf50' }} />,
              content: [
                '1. Click "Set Up" to go to campaigns page',
                '2. Choose your contact groups (who will receive the email)',
                '3. Select verified sender email and name',
                '4. Write compelling subject line',
                '5. Choose or create email template',
                '6. Preview your email on desktop and mobile',
                '7. Send test email to yourself first',
                '8. Schedule or send immediately'
              ]
            },
            {
              title: 'Subject Line Tips',
              icon: <TipsAndUpdatesIcon sx={{ color: '#f57c00' }} />,
              content: [
                'Keep it under 50 characters for mobile',
                'Create curiosity without being clickbait',
                'Avoid spam words like "FREE", "URGENT", "BUY NOW"',
                'Personalize when possible (use recipient name)',
                'A/B test different subject lines to see what works'
              ]
            }
          ],
          tips: [
            'Start with a small test group before sending to your entire list',
            'Best times to send: Tuesday-Thursday, 10 AM or 2 PM',
            'Monitor your metrics - aim for 20%+ open rate, 3%+ click rate'
          ],
          warnings: [
            'Never send to purchased email lists',
            'Always include an unsubscribe link',
            'Follow email marketing laws in your country'
          ]
        };

      default:
        return {
          icon: <InfoIcon sx={{ color: acme.colors.brand }} />,
          description: 'Get detailed information about this step.',
          sections: [],
          tips: [],
          warnings: []
        };
    }
  };

  const stepContent = getStepContent();

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {stepContent.icon}
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {stepTitle} - Complete Guide
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
          {stepContent.description}
        </Typography>

        {stepContent.sections.map((section, index) => (
          <Box key={index}>
            <SectionHeader>
              {section.icon}
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {section.title}
              </Typography>
            </SectionHeader>
            
            <List dense>
              {section.content.map((item, itemIndex) => (
                <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))}

        {stepContent.tips.length > 0 && (
          <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              💡 Pro Tips
            </Typography>
            {stepContent.tips.map((tip, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                • {tip}
              </Typography>
            ))}
          </Alert>
        )}

        {stepContent.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚠️ Important Notes
            </Typography>
            {stepContent.warnings.map((warning, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                • {warning}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
          Close Guide
        </Button>
        <BrandButton 
          onClick={onClose} 
          variant="contained" 
          startIcon={<LaunchIcon />}
        >
          Start Setup
        </BrandButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default InfoDialog; 